import { action, computed, observable } from 'mobx'
import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { unixEpoch } from '@/utils/date-util'
import type {
  SupportingItemInput,
  SupportingItemInputProps,
} from './supporting-item-input'
import { DocumentSupportingItemInput } from '@/features/jobs/info-gathering/documents/DocumentSupportingItemInput'
import { NotesSupportingItemInput } from '@/features/jobs/info-gathering/notes/NotesSupportingItemInput'
import { PersonalInfoSupportingItemInput } from '@/features/jobs/info-gathering/individual-info/PersonalInfoSupportingItemInput'
import { SpousalInfoSupportingItemInput } from '@/features/jobs/info-gathering/individual-info/SpousalInfoSupportingItemInput'
import { DependentsInfoSupportingItemInput } from '@/features/jobs/info-gathering/individual-info/DependentsInfoSupportingItemInput'
import { BusinessInfoSupportingItemInput } from '@/features/jobs/info-gathering/business-info/BusinessInfoSupportingItemInput'
import { PersonalRefundMethodInfoSupportingItemInput } from '@/features/jobs/info-gathering/tax-refund-method/PersonalRefundMethodInfoSupportingItemInput'
import { BusinessRefundMethodInfoSupportingItemInput } from '@/features/jobs/info-gathering/tax-refund-method/BusinessRefundMethodInfoSupportingItemInput'

/**
 * Props for {@link RequestedSupportingItem}.
 */
export type RequestedSupportingItemProps =
  ModelProps<RequestedSupportingItem> & {
    input: SupportingItemInputProps
  }

/**
 * A requested item on a checklist.
 */
export class RequestedSupportingItem<
  TInput extends SupportingItemInput = SupportingItemInput,
> extends Model<RequestedSupportingItem<TInput>> {
  @observable private accessor _input: SupportingItemInput | null = null

  id = Id.none

  @observable accessor name = ''
  @observable accessor heading = ''
  @observable accessor shortDescription = ''
  @observable accessor longDescription = ''
  @observable accessor imageUrl: string | null = null
  @observable accessor previewUrl: string | null = null
  @observable accessor required = false
  @observable accessor adHoc = false
  @observable accessor completed = false
  @observable accessor updatedAt = unixEpoch()

  @computed get input(): TInput | null {
    return this._input as TInput | null
  }

  /** @inheritDoc */
  @action.bound
  override set({
    input,
    ...props
  }: Partial<RequestedSupportingItemProps>): this {
    super.set(props)
    if (input) {
      this._input = this._updateInput(input)
    }

    return this
  }

  private _updateInput(input: SupportingItemInputProps): SupportingItemInput {
    switch (input.type) {
      case 'Document':
        return (
          this._input instanceof DocumentSupportingItemInput
            ? this._input
            : new DocumentSupportingItemInput()
        ).set(input)
      case 'PersonalInfo':
        return (
          this._input instanceof PersonalInfoSupportingItemInput
            ? this._input
            : new PersonalInfoSupportingItemInput()
        ).set(input)
      case 'SpousalInfo':
        return (
          this._input instanceof SpousalInfoSupportingItemInput
            ? this._input
            : new SpousalInfoSupportingItemInput()
        ).set(input)
      case 'DependentsInfo':
        return (
          this._input instanceof DependentsInfoSupportingItemInput
            ? this._input
            : new DependentsInfoSupportingItemInput()
        ).set(input)
      case 'PersonalRefundMethodInfo':
        return (
          this._input instanceof PersonalRefundMethodInfoSupportingItemInput
            ? this._input
            : new PersonalRefundMethodInfoSupportingItemInput()
        ).set(input)
      case 'BusinessInfo':
        return (
          this._input instanceof BusinessInfoSupportingItemInput
            ? this._input
            : new BusinessInfoSupportingItemInput()
        ).set(input)
      case 'BusinessRefundMethodInfo':
        return (
          this._input instanceof BusinessRefundMethodInfoSupportingItemInput
            ? this._input
            : new BusinessRefundMethodInfoSupportingItemInput()
        ).set(input)
      case 'Notes':
        return (
          this._input instanceof NotesSupportingItemInput
            ? this._input
            : new NotesSupportingItemInput()
        ).set(input)
    }
  }
}
